import React, { FC, useState, useEffect, useContext } from 'react';

import { Alert, Button, Card, CardContent, Grid, TextField, Typography } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';

import IconCardHeader from '../../../sharedModules/layout/IconCardHeader';
import InformationField from '../../../sharedModules/form/components/InformationField';

import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';

import axios from '../../../sharedModules/utils/axios.utils';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';

const UNKNOWN_CONTACT_SX = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(211, 47, 47, 0.1)',
  },
} as const;

interface ContactNameFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  editable: boolean;
  maxLength?: number;
}

const ContactNameField: FC<ContactNameFieldProps> = ({ label, value, onChange, disabled, editable, maxLength }) => {
  if (!editable) {
    return <InformationField label={label} value={value} />;
  }

  return (
    <TextField
      fullWidth
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      variant="outlined"
      size="small"
      disabled={disabled}
      sx={value === 'UNKNOWN' ? UNKNOWN_CONTACT_SX : undefined}
      inputProps={{ maxLength }}
    />
  );
};

interface ContactInfoUpdatingFieldsProps {
  contactId: string;
  firstName: string;
  lastName: string;
  fetchBackflowJobGroup: () => void;
  backflowJobGroupId: string;
}

const ContactInfoUpdatingFields: FC<ContactInfoUpdatingFieldsProps> = (props) => {
  const { contactId, firstName, lastName, fetchBackflowJobGroup, backflowJobGroupId } = props;
  const { showSuccessSnackBar } = useContext(SnackBarContext);
  const [firstNameValue, setFirstNameValue] = useState<string>('');
  const [lastNameValue, setLastNameValue] = useState<string>('');
  const [columnLimits, setColumnLimits] = useState<Record<string, number>>({});
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    setFirstNameValue(firstName || 'UNKNOWN');
    setLastNameValue(lastName || 'UNKNOWN');
  }, [firstName, lastName]);

  useEffect(() => {
    if (contactId) {
      axios
        .get<string, any>(`/sales_estimates/contacts/${contactId}`)
        .then((response) => setColumnLimits(response.data.result?.servman_column_limits || {}))
        .catch(() => {});
    }
  }, [contactId]);

  const handleUpdate = async () => {
    setLoading(true);

    try {
      await axios.put(`/sales_estimates/contacts/${contactId}`, {
        contact: { first_name: firstNameValue, last_name: lastNameValue },
      });
    } catch {
      setErrorMessage('Failed to update contact.');
      setLoading(false);
      return;
    }

    try {
      if (backflowJobGroupId) {
        await axios.post(`/backflows/backflow_job_groups/${backflowJobGroupId}/sync_ordhdr_contact`);
      }
      setErrorMessage('');
      showSuccessSnackBar('Contact updated successfully.');
    } catch {
      setErrorMessage('Contact saved, but failed to update ORDHDR in Servman.');
    } finally {
      fetchBackflowJobGroup();
      setLoading(false);
    }
  };

  // This case should not happen in production. This alert is for development records.
  if (!contactId) {
    return (
      <Alert severity="info">
        No contact is assigned to this Backflow Order. Assign a contact to update contact fields.
      </Alert>
    );
  }

  return (
    <Card elevation={3}>
      <CardContent>
        <IconCardHeader icon={PersonIcon} title="Contact" />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <ErrorMessage message={errorMessage} />
          </Grid>
          <Grid item xs={12} md={6}>
            <ContactNameField
              label="Contact First Name"
              value={firstNameValue}
              onChange={setFirstNameValue}
              disabled={loading}
              editable={firstName === 'UNKNOWN'}
              maxLength={columnLimits.first_name}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ContactNameField
              label="Contact Last Name"
              value={lastNameValue}
              onChange={setLastNameValue}
              disabled={loading}
              editable={lastName === 'UNKNOWN'}
              maxLength={columnLimits.last_name}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              Update the values for this app, and also for CONTACT and ORDHDR in Servman.
            </Typography>
          </Grid>
          <Grid item xs={12} textAlign="left">
            <Button variant="contained" style={{ marginTop: 15 }} onClick={handleUpdate}>
              Update
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default ContactInfoUpdatingFields;
